/**
 * InfraArt — inline SVG illustrations for every tile & token type.
 * Constraints (per design brief):
 *   • Flat solid colors only — no gradients, no strokes, no shadows
 *   • ≤ 3 distinct colors per illustration
 *   • Clean geometry, generous negative space
 *   • Legible at token size (~ quarter of a tile)
 *
 * Player ownership rings are painted OUTSIDE these components
 * (BoardGrid inset border for tiles, EdgeTokens ring for tokens),
 * so nothing in here touches player color.
 */

const TILE_VB = "0 0 120 120";
const TOKEN_VB = "0 0 30 30";

// ---------------------------------------------------------------
// TILE ILLUSTRATIONS (120×120)
// ---------------------------------------------------------------

function HouseCubeTile() {
  // Colors: warm stone bg + cream building + amber windows
  return (
    <svg viewBox={TILE_VB} className="absolute inset-0 w-full h-full">
      <rect x="0" y="0" width="120" height="120" fill="#7a7469" />
      <rect x="26" y="22" width="68" height="80" fill="#f0e6d2" />
      {/* windows — 3 rows × 2 cols */}
      <rect x="36" y="34" width="16" height="14" fill="#c9a13b" />
      <rect x="68" y="34" width="16" height="14" fill="#c9a13b" />
      <rect x="36" y="55" width="16" height="14" fill="#c9a13b" />
      <rect x="68" y="55" width="16" height="14" fill="#c9a13b" />
      <rect x="36" y="76" width="16" height="14" fill="#c9a13b" />
      <rect x="68" y="76" width="16" height="14" fill="#c9a13b" />
    </svg>
  );
}

function UrbanParkTile() {
  // Colors: lawn green + sand path + dark tree
  return (
    <svg viewBox={TILE_VB} className="absolute inset-0 w-full h-full">
      <rect x="0" y="0" width="120" height="120" fill="#5c8a5c" />
      {/* diagonal path */}
      <polygon points="0,88 32,120 120,52 120,20 88,20" fill="#e6ddc8" />
      {/* tree canopy — big circle */}
      <circle cx="34" cy="42" r="20" fill="#2a5530" />
      {/* second smaller canopy */}
      <circle cx="88" cy="94" r="12" fill="#2a5530" />
    </svg>
  );
}

function UrbanForestTile() {
  // Colors: dark forest bg + mid canopy + tiny cream clearing
  return (
    <svg viewBox={TILE_VB} className="absolute inset-0 w-full h-full">
      <rect x="0" y="0" width="120" height="120" fill="#1f4022" />
      {/* overlapping tree canopies */}
      <circle cx="30" cy="34" r="22" fill="#4a7a4a" />
      <circle cx="82" cy="30" r="24" fill="#4a7a4a" />
      <circle cx="60" cy="72" r="26" fill="#4a7a4a" />
      <circle cx="24" cy="90" r="18" fill="#4a7a4a" />
      <circle cx="94" cy="90" r="18" fill="#4a7a4a" />
      {/* small cream clearing */}
      <circle cx="60" cy="72" r="8" fill="#f0e6d2" />
    </svg>
  );
}

function MangroveForestTile() {
  // Colors: water blue + mangrove canopy + dark roots
  return (
    <svg viewBox={TILE_VB} className="absolute inset-0 w-full h-full">
      <rect x="0" y="0" width="120" height="120" fill="#356580" />
      {/* three mangrove tufts */}
      <circle cx="30" cy="52" r="18" fill="#3f6b3a" />
      <circle cx="70" cy="42" r="20" fill="#3f6b3a" />
      <circle cx="98" cy="58" r="14" fill="#3f6b3a" />
      {/* roots — thin verticals reaching down */}
      <rect x="22" y="66" width="3" height="34" fill="#1a2b1a" />
      <rect x="30" y="66" width="3" height="38" fill="#1a2b1a" />
      <rect x="38" y="66" width="3" height="32" fill="#1a2b1a" />
      <rect x="62" y="58" width="3" height="46" fill="#1a2b1a" />
      <rect x="70" y="58" width="3" height="50" fill="#1a2b1a" />
      <rect x="78" y="58" width="3" height="42" fill="#1a2b1a" />
      <rect x="94" y="70" width="3" height="30" fill="#1a2b1a" />
      <rect x="102" y="70" width="3" height="34" fill="#1a2b1a" />
    </svg>
  );
}

function InlandWetlandTile() {
  // Colors: water + reed dark + mud strip
  return (
    <svg viewBox={TILE_VB} className="absolute inset-0 w-full h-full">
      <rect x="0" y="0" width="120" height="120" fill="#4a7a95" />
      {/* mud strip across middle */}
      <rect x="0" y="78" width="120" height="14" fill="#6b5540" />
      {/* reeds — vertical thin rects rising from mud */}
      <rect x="14" y="42" width="3" height="42" fill="#1f4022" />
      <rect x="22" y="34" width="3" height="50" fill="#1f4022" />
      <rect x="34" y="46" width="3" height="38" fill="#1f4022" />
      <rect x="52" y="30" width="3" height="54" fill="#1f4022" />
      <rect x="60" y="40" width="3" height="44" fill="#1f4022" />
      <rect x="76" y="34" width="3" height="50" fill="#1f4022" />
      <rect x="88" y="44" width="3" height="40" fill="#1f4022" />
      <rect x="102" y="36" width="3" height="48" fill="#1f4022" />
    </svg>
  );
}

// ---------------------------------------------------------------
// TOKEN ILLUSTRATIONS (30×30 — will sit inside a circular clip)
// ---------------------------------------------------------------

function LowSeaWallToken() {
  // Colors: pale sand bg + stone wall + dark accent
  return (
    <svg viewBox={TOKEN_VB} className="w-full h-full">
      <rect x="0" y="0" width="30" height="30" fill="#e6ddc8" />
      <rect x="4" y="18" width="22" height="6" fill="#7a7469" />
      <rect x="4" y="17" width="22" height="1.5" fill="#2b2620" />
    </svg>
  );
}

function GreenRoofToken() {
  // Colors: neutral bg + house + green vegetated roof
  return (
    <svg viewBox={TOKEN_VB} className="w-full h-full">
      <rect x="0" y="0" width="30" height="30" fill="#e6ddc8" />
      {/* house body */}
      <rect x="7" y="14" width="16" height="10" fill="#7a7469" />
      {/* green roof — flat, slightly overhanging */}
      <rect x="5" y="9" width="20" height="5" fill="#3f6b3a" />
      {/* couple of foliage dots */}
      <circle cx="10" cy="8" r="1.5" fill="#3f6b3a" />
      <circle cx="20" cy="8" r="1.5" fill="#3f6b3a" />
    </svg>
  );
}

function GreenStreetToken() {
  // Street tree on a green verge, with a swale along the road
  return (
    <svg viewBox={TOKEN_VB} className="w-full h-full">
      <rect x="0" y="0" width="30" height="30" fill="#5E8F5A" />
      <circle cx="15" cy="12" r="6" fill="#3f6b3a" />
      <rect x="14" y="17" width="2" height="6" fill="#6b5540" />
      <rect x="3" y="24" width="24" height="3" rx="1.5" fill="#3E8FA0" />
    </svg>
  );
}

// ---------------------------------------------------------------
// Public API
// ---------------------------------------------------------------

const TILE_COMPONENTS = {
  house_cube: HouseCubeTile,
  urban_park: UrbanParkTile,
  urban_forest: UrbanForestTile,
  mangrove_forest: MangroveForestTile,
  inland_wetland: InlandWetlandTile,
};

const TOKEN_COMPONENTS = {
  low_sea_wall: LowSeaWallToken,
  green_roof: GreenRoofToken,
  green_street: GreenStreetToken,
};

// Fallback: category-tinted flat square for any card without dedicated art.
function FallbackTile({ category }) {
  const bg =
    category === "green" ? "#3e6b3e" : category === "blue" ? "#356580" : "#7a7469";
  return (
    <svg viewBox={TILE_VB} className="absolute inset-0 w-full h-full">
      <rect x="0" y="0" width="120" height="120" fill={bg} />
    </svg>
  );
}

function FallbackToken({ category }) {
  const bg =
    category === "green" ? "#3e6b3e" : category === "blue" ? "#356580" : "#7a7469";
  return (
    <svg viewBox={TOKEN_VB} className="w-full h-full">
      <rect x="0" y="0" width="30" height="30" fill={bg} />
    </svg>
  );
}

export function TileArt({ cardKey, category = "gray" }) {
  const Comp = TILE_COMPONENTS[cardKey];
  if (Comp) return <Comp />;
  return <FallbackTile category={category} />;
}

export function TokenArt({ cardKey, category = "gray" }) {
  const Comp = TOKEN_COMPONENTS[cardKey];
  if (Comp) return <Comp />;
  return <FallbackToken category={category} />;
}

export const TOKEN_KEYS = Object.keys(TOKEN_COMPONENTS);
